<template>
  <div
    ref="partialDiv"
    :asset-name="slug"
  >
    <section class="signup-success">
      <div
        :class="{
          'container mx-auto my-6': true,
        }"
      >
        <h2
          :class="{
            'signup-title': true,
          }"
        >
          {{ signupTitle }}
        </h2>

        <div
          :class="{
            'signup-msg success-msg mt-32px': true,
            'flex justify-center': true,
          }"
        >
          <div
            :class="{
              'w-[462px] rounded-md border border-[#dcdfeb] transition-all duration-300 ease-in-out hover:border-www-color-primary-lighter hover:shadow-card': true,
            }"
          >
            <div
              :class="{
                'card-body p-[25px]': true,
              }"
            >
              <ImageAtom
                v-if="image"
                v-bind="image"
                loading="eager"
              />

              <h3
                :class="{
                  'mt-6 text-center text-lg font-bold': true,
                }"
              >
                {{ cardTitle }}
              </h3>

              <p
                :class="{
                  email: true,
                  'text-center': true,
                }"
              >
                {{ emailValue }}
              </p>
            </div>
            <div
              :class="{
                'card-footer flex flex-col items-center border-t border-[#dcdfeb] bg-[#dcdfeb] bg-opacity-20 p-[15px] text-www-color-neutral-medium-dark': true,
              }"
            >
              <h6
                :class="{
                  'text-15px font-medium': true,
                }"
              >
                {{ cardSubtitle }}
              </h6>

              <ul
                :class="{
                  'list-disc px-6': true,
                }"
              >
                <template
                  v-for="(item, i) in signupMessageList"
                  :key="i"
                >
                  <li
                    :class="{
                      'link w-fit text-13px': true,
                    }"
                  >
                    <CodeEmbed v-bind="item" />
                  </li>
                </template>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <slot />
    </section>
    <template v-if="screenPartialData?.sectionBuilder?.length > 0">
      <component
        :is="getComponent(component._type)"
        v-for="component in screenPartialData?.sectionBuilder"
        :key="component._key"
        :value="component"
      />
    </template>
  </div>
</template>

<script lang="ts" setup>
import { FieldHtmlNameUtil } from '@onshape/www-inertia';
import ImageAtom from '~/components/Atmos/ImageAtom/ImageAtom.vue';
import CodeEmbed from '~/components/Atmos/CodeEmbed/CodeEmbed.vue';
import UnifiedSignupAgent from '~/hekate-www/external/facets/Form/coordinator/UnifiedSignup/UnifiedSignupAgent';
import type UnifiedSignupSessionInfo from '~/hekate-www/external/facets/Form/coordinator/UnifiedSignup/session-info/UnifiedSignupSessionInfo';
import type SubmittingFormLoadingSignupScreen from '~/hekate-www/external/facets/Form/coordinator/UnifiedSignup/screens/EmailStatusSignupScreen';
import type { SignupPartialViewsSectionBuilder, UssMessage } from '~/sanity/types';
import FluentWaitUtil from '~/utils/FluentWaitUtil';
import UnifiedSignupPersonaDetails from '~/hekate-www/external/facets/Form/coordinator/UnifiedSignup/session-info/UnifiedSignupPersonaDetails';
import UnifiedSignupPersona from '~/hekate-www/external/facets/Form/coordinator/UnifiedSignup/session-info/UnifiedSignupPersona';
import UnifiedSignupPlan from '~/hekate-www/external/facets/Form/coordinator/UnifiedSignup/session-info/UnifiedSignupPlan';
import UnifiedSignupIntent from '~/hekate-www/external/facets/Form/coordinator/UnifiedSignup/session-info/UnifiedSignupIntent';
import { getComponent } from '~/components/Mapper.js';

const props = defineProps<
  UssMessage & {
    signupScreen: SubmittingFormLoadingSignupScreen;
    sessionInfo: UnifiedSignupSessionInfo;
    signupSurveyDiv?: HTMLDivElement | null;
    screenPartialData: SignupPartialViewsSectionBuilder;
  }
>();

const emit = defineEmits<{
  updatePage: [];
  hideNextButton: [];
  setEmailStatus: [status: string];
}>();

// Template ref for DOM manipulation
const partialDiv = ref<HTMLDivElement | null>(null);

// Get email value from form field
const emailField = props.signupScreen.getField(FieldHtmlNameUtil.email);
const emailValue = computed(() => emailField.val as string);

// Track polling interval for cleanup
let pollingIntervalId: ReturnType<typeof setInterval> | null = null;

// Poll email status
const pollEmailStatus = async () => {
  const email = emailField.val as string;
  const job: Promise<boolean> = new Promise((res) => {
    let maxTries = 30;
    pollingIntervalId = setInterval(async () => {
      maxTries -= 1;
      if (maxTries < 1) {
        emit('setEmailStatus', 'INVALID');
        clearInterval(pollingIntervalId!);
        pollingIntervalId = null;
        return res(true);
      }
      const status = await UnifiedSignupAgent.emailStatus({
        email,
      });

      console.log('status.emailStatusState', status.emailStatusState);
      if (status.emailStatusState === 'PENDING_VERIFICATION') {
        return;
      }
      emit('setEmailStatus', status.emailStatusState);
      clearInterval(pollingIntervalId!);
      pollingIntervalId = null;
      return res(true);
    }, 2000);
  });
  await job;
};

// Initialize survey visibility - waits for both resolutionActions AND DOM elements to be ready
const initSurveyVisibility = async (): Promise<void> => {
  if (!partialDiv.value) {
    return;
  }

  // Wait for resolutionActions from server (up to 5 seconds)
  let replacement: Node = document.createTextNode('');
  let procedure = '';
  const resolutions = await FluentWaitUtil.whileNotFalsy({
    maxTries: 50,
    timeBetweenChecksInMilliseconds: 100,
    provider: () => props.sessionInfo.resolutionActions,
  }).catch(() => {
    console.warn(
      `Timed out waiting for resolutionActions on screen: ${props.signupScreen.screenName}`
    );
    return null;
  });

  // If we are not on the success screen and we haven't resolved the resolutions, we will not show the survey
  if (resolutions === null) {
    console.warn(
      `Timed out waiting for resolutionActions on screen: ${props.signupScreen.screenName}`
    );
    return;
  }

  // Wait for DOM update and survey elements to be rendered by CodeEmbed
  await nextTick();
  await FluentWaitUtil.whileNotFalsy({
    maxTries: 50,
    timeBetweenChecksInMilliseconds: 100,
    provider: () => partialDiv.value?.querySelector('#signup-survey-youtube-link'),
  }).catch(() => {
    console.warn('Survey elements not found in DOM');
  });

  procedure = resolutions[0]?.procedure || '';
  replacement = document.createTextNode(resolutions[0]?.params?.replacement || '');

  const { personaDetails: chosenPersonaDetails } = props.sessionInfo;
  const { chosenPlan } = props.sessionInfo;
  const { intent: chosenIntent } = props.sessionInfo;
  const { persona: chosenPersona } = props.sessionInfo;
  if (!replacement || !chosenPlan || !chosenIntent || !chosenPersona) {
    console.warn(
      `Missing required information, not showing survey on non-success screen: ${props.signupScreen.screenName}`
    );
    return;
  }

  // If persona is startup, show startup survey
  if (chosenPersonaDetails?.compareTo(UnifiedSignupPersonaDetails.startingNewBusiness)) {
    const signupSurveyStartupProgramElement = partialDiv.value.querySelector(
      '#signup-survey-startup-program'
    );
    if (signupSurveyStartupProgramElement) {
      signupSurveyStartupProgramElement.classList.remove('d-none');
    }
    // If persona is pro or biz intent show link to upcoming events
  } else if (
    UnifiedSignupPlan.pro.compareTo(chosenPlan) ||
    UnifiedSignupIntent.bizIntent.compareTo(chosenIntent) ||
    UnifiedSignupPersona.proPerson.compareTo(chosenPersona)
  ) {
    const signupSurveyUpcomingSessionsElement = partialDiv.value.querySelector(
      '#signup-survey-upcoming-sessions'
    );
    if (signupSurveyUpcomingSessionsElement) {
      signupSurveyUpcomingSessionsElement.classList.remove('d-none');
    }
    // If not pro and procedure is tellMeMoreAboutIt, show tell us more
  } else if (procedure === 'tellMeMoreAboutIt' && !UnifiedSignupPlan.pro.compareTo(chosenPlan)) {
    if (!props.signupSurveyDiv) {
      console.error('Signup survey div not set');
      return;
    }

    // Show tell us more survey section and populate with replacement content
    const signupSurveyTellUsMoreElement = partialDiv.value.querySelector(
      '#signup-survey-tell-us-more'
    );
    if (signupSurveyTellUsMoreElement) {
      signupSurveyTellUsMoreElement.classList.remove('d-none');
    }
    partialDiv.value.querySelector('#hekate-tell-more-about-replacement')?.appendChild(replacement);
    partialDiv.value
      .querySelector('#signup-survey-tell-us-more')
      ?.appendChild(props.signupSurveyDiv);

    const howDidYouHearAboutUsElement = partialDiv.value.querySelector(
      '[field-name="howDidYouHearAboutUs"]'
    );
    if (howDidYouHearAboutUsElement) {
      const labelElement = howDidYouHearAboutUsElement.querySelector('label') as HTMLLabelElement;
      labelElement.innerHTML = '';
    }

    const signupSurveyElement = partialDiv.value.querySelector('#signup-survey');
    if (signupSurveyElement) {
      signupSurveyElement.classList.remove('d-none');
    }

    const signupSurveySurveyFormElement = partialDiv.value.querySelector(
      '#signup-survey-surveyForm'
    );
    if (signupSurveySurveyFormElement) {
      signupSurveySurveyFormElement.classList.remove('d-none');
    }
  } else {
    // Default fallback when none of the above match
    const signupSurveyYoutubeLinkElement = partialDiv.value.querySelector(
      '#signup-survey-youtube-link'
    );
    if (signupSurveyYoutubeLinkElement) {
      signupSurveyYoutubeLinkElement.classList.remove('d-none');
    }
  }
};

// Prevent duplicate survey initialization since multiple CodeEmbed components fire 'code-embed-rendered'
let surveyInitialized = false;

const tryInitSurvey = async (): Promise<void> => {
  if (surveyInitialized) {
    return;
  }
  surveyInitialized = true;
  await initSurveyVisibility();
};

onMounted(async () => {
  // Hide next button during polling
  emit('hideNextButton');
  // Listen for CodeEmbed render event to initialize survey
  partialDiv.value?.addEventListener('code-embed-rendered', tryInitSurvey, { once: true });

  // Poll email status
  await pollEmailStatus();
  // Trigger page update
  emit('updatePage');
});

// Cleanup polling interval and event listener on unmount to prevent memory leaks
onUnmounted(() => {
  if (pollingIntervalId !== null) {
    clearInterval(pollingIntervalId);
    pollingIntervalId = null;
  }
  partialDiv.value?.removeEventListener('code-embed-rendered', tryInitSurvey);
});

const { slug } = useRoute().params;
</script>
